import React from "react";
import FlexboxContainerComponent from "components/UI/FlexboxContainer";
import Page from "components/UI/Page";
import Breadcrumbs from 'components/UI/Breadcrumb';
import { navigate, usePath } from "hooks/useHookRouterCompat";
import useBreadcrumbs from "hooks/useBreadcrumbs";
import { BorderRadius } from "lib/styles";
import styled from "styled-components";
import { Space } from "styles/themeSpaces";
import Vendor from "data/Vendor";
import Button from "components/UI/Button";
import { Main } from "styles/themeColors";
import { GENERAL_CONSTANTS } from "constants/general";
import TabsComponent from "components/UI/Tabs";
import { BUSINESS_UNIT_DETAILS_TABS } from "constants/tabs";
import { useBusinessUnitForm } from "hooks/useBusinessUnitForm";
import AboutBusinessUnit from "./AboutBusinessUnit";
import LoadingIndicator from "components/UI/LoadingIndicator";
import { useModalState, ModalType } from "hooks/useAddonModalState";
import ConfirmationModal from 'components/UI/ConfimationModal';
import { useBusinessUnitActions } from "hooks/useBusinessUnitAction";
import UsersBusinessUnit from "./UsersBusinessUnit";
import { USERS_BUSINESS_UNIT_CONSTANTS } from "constants/usersBusinessUnit";
import BusinessUnitUserInviteModal from "./BusinessUnitUserInviteModal";
import { PermissionCode } from "data/UserPermissions";
import { useUserInvitation } from "hooks/useUserInvitation";
import FeatureFlag from "data/FeatureFlag";
import { BusinessUnit } from "data/BusinessUnit";

const BreadcrumbContainer = styled(FlexboxContainerComponent)`
  cursor: pointer;
  margin-left: ${Space.spHalf};
  margin-right: ${Space.spHalf};
  border-radius: ${BorderRadius.medium};

  &:hover {
    background: rgba(123, 97, 255, 0.05);
  }
`;

const Heading = styled.span<any>`
  font-size: 24px;
  margin-bottom: 20px;
  font-weight: 600;
`;

interface Props {
    businessUnitId?: string;
    selectedVendor?: Vendor;
    featureFlags: FeatureFlag;
    selectedBusinessUnit?: BusinessUnit;
};

const customPathNames = {
    'organizations': GENERAL_CONSTANTS.BUSINESS_UNITS,
};

const BusinessUnitDetails = (props: Props) => {

    const { businessUnitId, selectedVendor, featureFlags, selectedBusinessUnit } = props;
    const path = usePath();
    const crumbs = useBreadcrumbs(path, customPathNames);
    const [activeTab, setActiveTab] = React.useState<number>(0);
    const [reload, setReload] = React.useState(false);

    const {
        showInviteModal,
        inviteForm,
        inviteErrors,
        alreadyInvitedRole,
        userProfileId,
        isLoading: inviteLoading,
        toggleInviteModal,
        handleInviteSend,
        handleInviteInputChange,
        setUserProfileId,
        setAlreadyInvitedRole
    } = useUserInvitation({
        businessUnitId: businessUnitId,
        vendorId: selectedVendor?.id?.toString(),
        onSuccess: () => setReload(true)
    });

    const { modalState, showModal, hideModal } = useModalState([
        'unsavedChanges'
    ]);

    const {
        businessUnit,
        businessUnitDetailReq,
    } = useBusinessUnitActions({})

    const {
        control,
        handleSubmit,
        watch,
        errors,
        onSubmit,
        isLoading,
        setError,
        clearErrors,
        trigger,
        isDirty,
        vendorBrands,
        disableFormFields
    } = useBusinessUnitForm(selectedVendor!, businessUnit!, featureFlags);  // Using the custom hook

    React.useEffect(() => {
        if (businessUnitId && selectedVendor) {
            businessUnitDetailReq.run(businessUnitId, selectedVendor?.id);
        }
    }, [businessUnitId, selectedVendor]);

    const handleTabChange = (index: number | undefined) => {
        setActiveTab(index!);
    };

    const businessName = (
        <BreadcrumbContainer padding={Space.spBase}>
            {businessUnit?.name}
        </BreadcrumbContainer>
    );

    const onSave = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        const isFormValid = await trigger(); // Validate all fields
        if (!isFormValid) return;

        handleSubmit((data) => onSubmit({ ...data, vendorId: selectedVendor?.id }))();
    }

    const onCancel = () => {
        if (isDirty) {
            // Show confirmation modal if there are unsaved changes
            showModal('unsavedChanges');
        } else {
            // Navigate directly if no changes
            navigate('/organizations');
        }

    };

    const handleConfirmNavigation = () => {
        hideModal('unsavedChanges');
        navigate('/organizations');
    };

    const modalConfigs = {
        unsavedChanges: {
            title: GENERAL_CONSTANTS.UNSAVED_CHANGES,
            message: GENERAL_CONSTANTS.UNSAVE_CHANGES_DESC,
            onConfirm: handleConfirmNavigation
        }
    };

    if (isLoading || businessUnitDetailReq.isLoading) return <LoadingIndicator />;

    return (
        <Page>
            {Object.entries(modalConfigs).map(([key, config]) => (
                <ConfirmationModal
                    key={key}
                    show={modalState[key as ModalType]}
                    onClose={() => hideModal(key as ModalType)}
                    onConfirm={config.onConfirm}
                    title={config.title}
                    message={config.message}
                    confirmText={GENERAL_CONSTANTS.CONFIRM}
                    cancelText={GENERAL_CONSTANTS.CANCEL}
                />
            ))}
            <Breadcrumbs containerPadding="0 0 24px 8px" crumbs={crumbs} splitOrderName={businessName} />
            <FlexboxContainerComponent justifyContent='space-between' width='100%' alignItems='center' alignContent="center">
                <Heading className='heading'>{businessUnit?.name}</Heading>
                {
                    activeTab === 0 &&
                    <FlexboxContainerComponent gap='8px'>
                        <Button
                            onClick={onCancel}
                            bgColor={Main.white}
                            borderColor={Main.lightGray}
                            color={Main.black}
                        >
                            {GENERAL_CONSTANTS.CANCEL}
                        </Button>
                        <Button
                            onClick={onSave}
                            bgColor={Main.brandDark}
                            borderColor={Main.brandDark}
                            permissionCode={PermissionCode.EDIT_REMOVE_BUSINESS_UNIT}
                        >
                            {GENERAL_CONSTANTS.SAVE}
                        </Button>

                    </FlexboxContainerComponent>
                }

                {
                    activeTab === 1 &&
                    <FlexboxContainerComponent gap='8px'>
                        <Button
                            onClick={toggleInviteModal}
                            bgColor={Main.brandDark}
                            borderColor={Main.brandDark}
                            permissionCode={PermissionCode.INVITE_BUSINESS_UNIT}
                        >
                            {USERS_BUSINESS_UNIT_CONSTANTS.INVITE_USER}
                        </Button>
                    </FlexboxContainerComponent>
                }
            </FlexboxContainerComponent>

            <TabsComponent
                tabs={BUSINESS_UNIT_DETAILS_TABS}
                theme='brandDark'
                borderRadius='20px'
                margin='8px 24px 8px 0'
                height='auto'
                activeTab={activeTab}
                setActiveTab={handleTabChange}
                className="brands-tabs"
            >
                <AboutBusinessUnit
                    control={control}
                    brands={vendorBrands!}
                    errors={errors}
                    watch={watch}
                    setError={setError}
                    clearErrors={clearErrors}
                    disabled={disableFormFields}
                />
                <UsersBusinessUnit
                    businessUnitId={businessUnitId}
                    setUserProfileId={setUserProfileId}
                    onInputChange={handleInviteInputChange}
                    toggleInviteModal={toggleInviteModal}
                    reload={reload}
                    setReload={setReload}
                    selectedBusinessUnit={selectedBusinessUnit}
                />
            </TabsComponent>
            <BusinessUnitUserInviteModal
                show={showInviteModal}
                onClose={toggleInviteModal}
                onSendInvitation={handleInviteSend}
                inviteForm={inviteForm}
                onInputChange={handleInviteInputChange}
                errors={inviteErrors}
                isEdit={!!userProfileId}
                alreadyInvitedRole={alreadyInvitedRole}
                setAlreadyInvitedRole={setAlreadyInvitedRole}
                isLoading={inviteLoading}
                salesOrderFeatureFlag={featureFlags?.salesOrder?.enabled!}
            />
        </Page>
    );
}

export default BusinessUnitDetails;